import api from '@/services/api';

const state = {
  user: null,
  token: null,
  roles: [],
  claims: [],
  flags: [],
  facilities: [],
  selectedFacility: null,
  tempLogin: null,
  backgroundColor: 'bg-white',
};

const mutations = {
  setUser(state, user) {
    state.user = user;
  },
  setToken(state, token) {
    state.token = token;
  },
  setRoles(state, roles) {
    state.roles = roles;
  },
  setClaims(state, claims) {
    state.claims = claims;
  },
  setFlags(state, flags) {
    state.flags = flags;
  },
  setFacilities(state, facilities) {
    state.facilities = facilities;
  },
  setSelectedFacility(state, facility) {
    state.selectedFacility = facility;
  },
  setTempLogin(state, loginData) {
    state.tempLogin = loginData;
  },
  clearTempLogin(state) {
    state.tempLogin = null;
  },
  setBackgroundColor(state, color) {
    state.backgroundColor = color;
  },
};

const actions = {
  async initializeAuth({ commit, dispatch, state }) {
    const token = localStorage.getItem('token');
    const facilityId = localStorage.getItem('facilityId');
    if (token && !state.token) {
      try {
        await dispatch('fetchUser');
        if (facilityId) {
          const facility = state.facilities.find(f => f.facility_id === facilityId);
          if (facility) {
            commit('setSelectedFacility', facility);
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        dispatch('logout');
      }
    }
  },

  toggleBackgroundColor({ commit, state }) {
    const newColor = state.backgroundColor === 'bg-white' ? 'bg-stone-100' : 'bg-white';
    commit('setBackgroundColor', newColor);
  },

  preLogin({ commit }, { data, token, roles, flags, facilities }) {
    const loginData = { data, token, roles, flags, facilities };
    commit('setTempLogin', loginData);
    commit('setFacilities', facilities || []);
  },

  completeLogin({ commit, state }, { facilityId, token, facility, roles, flags }) {
    const { data } = state.tempLogin || {};
    if (!data || !token || !facilityId || !facility) {
      throw new Error('Invalid login state');
    }

    commit('setUser', data);
    commit('setToken', token);
    commit('setRoles', roles || []);
    commit('setFlags', flags || []);
    commit('setSelectedFacility', facility);
    const claims = roles ? roles.flatMap(role => role.claims || []) : [];
    commit('setClaims', claims);
    localStorage.setItem('token', token);
    localStorage.setItem('facilityId', facilityId);
    commit('clearTempLogin');
  },

  async fetchUser({ commit }) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token found');
    }
    try {
      const response = await api.get('/user', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const { user, roles, flags, facilities } = response.data;
      commit('setUser', user);
      commit('setToken', token);
      commit('setRoles', roles || []);
      commit('setFlags', flags || []);
      commit('setFacilities', facilities || []);
      const claims = roles ? roles.flatMap(role => role.claims || []) : [];
      commit('setClaims', claims);
      const facilityId = localStorage.getItem('facilityId');
      if (facilityId) {
        const facility = facilities.find(f => f.facility_id === facilityId);
        if (facility) {
          commit('setSelectedFacility', facility);
        }
      }
      return user;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      commit('setUser', null);
      commit('setToken', null);
      commit('setRoles', []);
      commit('setClaims', []);
      commit('setFlags', []);
      commit('setFacilities', []);
      commit('setSelectedFacility', null);
      localStorage.removeItem('token');
      localStorage.removeItem('facilityId');
      throw error;
    }
  },

  logout({ commit }) {
    commit('setUser', null);
    commit('setToken', null);
    commit('setRoles', []);
    commit('setClaims', []);
    commit('setFlags', []);
    commit('setFacilities', []);
    commit('setSelectedFacility', null);
    localStorage.removeItem('token');
    localStorage.removeItem('facilityId');
  },
};

const getters = {
  user: state => state.user,
  token: state => state.token,
  roles: state => state.roles,
  claims: state => state.claims,
  flags: state => state.flags,
  facilities: state => state.facilities,
  selectedFacility: state => state.selectedFacility,
  hasRole: state => roleName => state.roles.some(role => role.name === roleName),
  hasClaim: state => claim => state.claims.includes(claim),
  hasFlag: state => flag => state.flags.includes(flag),
  isAuthenticated: state => !!state.user && !!state.token && !!state.selectedFacility,
  backgroundColor: state => state.backgroundColor,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};